const MATCH = {
    ROW: 4100,
    USA: 4905,
};

const TUBE = {
    ROW: 5014,
    USA: 5024,
};

const ALGORITHM = {
    ROW: 4994,
    USA: 5003,
};

const WIREFLOW = {
    ROW: 4160,
    USA: 4767,
};

export const PLUSMINUS = {
    ROW: 5031,
    USA: 5044,
    SUBFAMILY_ID: 225,
    ESSENTIALS_SUBFAMILY_ID: 266,
};

const STICKS = {
    ROW: 5025,
    USA: 5037,
};

const ORIGAMI = {
    ROW: 4075,
    USA: 4089,
};

const STICKS_SOLO = {
    SUBFAMILY_ID: 247,
}

const PLUSMINUS_SOLO = {
    SUBFAMILY_ID: 246,
}

const CATEGORIES = {
    REGULAR: 1,
    SPECIFIC_PLAYER: 2,
    MODULAR: 3,
    LG2: 4,
    VIRTUAL: 5,
    TOOLKIT: 6,
    TOOLKIT_ALT: 7
}

const PRODUCT_TYPE = {
    REGULAR: 0,
    VIRTUAL: 1,
    TOOLKIT: 2
}

const FAMILIES = {
    match: [MATCH.ROW, MATCH.USA],
    tube: [TUBE.ROW, TUBE.USA],
    algorithm: [ALGORITHM.ROW, ALGORITHM.USA],
    wireflow: [WIREFLOW.ROW, WIREFLOW.USA],
    plusminus: [PLUSMINUS.ROW, PLUSMINUS.USA],
    sticks: [STICKS.ROW, STICKS.USA],
    origami: [ORIGAMI.ROW, ORIGAMI.USA]
};

export default class Product {
    props: any;

    constructor(props: any) {
        this.props = props;
    }

    getProps() {
        return JSON.parse(JSON.stringify(this.props));
    }

    getProp(prop: string) {
        return this.props[prop];
    }

    setProp(prop: string, value: any) {
        this.props[prop] = value;
        return this;
    }

    private isFamily(familyName: string, familyId: number): boolean {
        if (FAMILIES[familyName]) {
            return FAMILIES[familyName].includes(familyId);
        }
        return false;
    }

    /** DV-6704 — Sticks and Tube lines show the packaging of each composition when they are ordered (sr-orders `canIncludePackaging`). */
    showsPackagingSummary(): boolean {
        return this.isFamily('sticks', this.props.familyId) || this.isFamily('tube', this.props.familyId);
    }

    private isSticksSolo(familyId: number, subFamilyId: number): boolean {
        return this.isFamily('sticks', familyId) && STICKS_SOLO.SUBFAMILY_ID === subFamilyId;
    }

    private isPlusminusSolo(familyId: number, subFamilyId: number): boolean {
        return this.isFamily('plusminus', familyId) && PLUSMINUS_SOLO.SUBFAMILY_ID === subFamilyId;
    }

    private toolkitDescription(): string {
        const totalCompositionLuminaires = this.props.compositions?.map(comp => {
            const quantities = comp.articles?.map(article => article.quantity ?? 0) ?? [];
            const luminariesPerComposition = quantities.reduce((acc, curr) => acc + curr, 0);
            return luminariesPerComposition;
        }) ?? [];

        const totalLuminaires = totalCompositionLuminaires.reduce((acc, curr) => acc + curr, 0);

        const compositionNumber = this.props.compositionNumber ?? this.props.compositions?.length;
        const luminaires = this.props.luminaires ?? totalLuminaires;

        let extraDescription = "";
        if (this.props.compositions?.length === 1) {
            extraDescription = [
                this.props.compositions[0]?.attributes?.finish?.description,
                this.props.compositions[0]?.totalBeltLength?.shortDescription,
                this.props.compositions[0]?.attributes?.installation?.description,
                this.props.compositions[0]?.attributes?.control?.description,
                this.props.compositions[0]?.plusminusAccessory?.watts?.description,
                this.props.compositions[0]?.accessories?.length ? `Accessory ${this.props.compositions[0]?.accessories.length} set` : ''
            ].filter(Boolean).join(', ');
        }

        return `${this.props.subFamily?.description}. ${this.props.creationCode}-${compositionNumber}. Set ${luminaires} Luminaires. ${extraDescription}`;
    }

    private regularAndToolkitDescription(): string {
        if (this.isSticksSolo(this.props.familyId, this.props.subFamilyId) ||
            this.isPlusminusSolo(this.props.familyId, this.props.subFamilyId) ||
            (this.isFamily('sticks', this.props.familyId) && !this.props.creationCode)
        ) {
            return this.props.shortDescription;
        } else {
            return this.toolkitDescription();
        }
    }

    private tubeVirtualDescription(): string {
        const numberOfShades = this.props.compositions
            .flatMap((composition) =>
                (Array.isArray(composition.articles) ? composition.articles : []).flatMap((article) =>
                    article.parts.filter((part) => part.articleType?.level3?.id === 3)
                )
            )
            .reduce((totalShades, part) => totalShades + part.quantity, 0);

        return `${this.props.creationCode} (${numberOfShades} shades)`;
    }

    private getBaseCrmDescription(): string {
        const familyId = this.props.familyId;
        const categoryId = this.props.category?.id;

        switch (true) {
            case this.isFamily('origami', familyId) && this.props.type === "CREATION":
                return this.toolkitDescription();

            case this.isFamily('match', familyId):
                return `${this.props?.creationCode} ${this.props?.finish?.description || ''}`;

            case this.isFamily('tube', familyId):
                if (categoryId === CATEGORIES.VIRTUAL) {
                    return this.tubeVirtualDescription();
                } else {
                    return this.props?.shortDescription || '';
                }

            case this.isFamily('algorithm', familyId):
                if (categoryId === CATEGORIES.VIRTUAL) {
                    return `${this.props.creationCode} ${this.props.finish?.description || ''}`;
                } else if (categoryId === CATEGORIES.LG2) {
                    return `${this.props.compositions[0]?.articles[0].articleCode} ${this.props.compositions[0]?.articles[0]?.shortDescription}` || '';
                } else {
                    // Para Algorithm TOOLKIT, usar toolkitDescription
                    return this.toolkitDescription();
                }

            case this.isFamily('wireflow', familyId):
                const subDescription = (this.props.compositions?.[0]?.articles[0]?.articleCode) ?? this.props.shortDescription ?? this.props.creationCode;
                return `WIREFLOW ${subDescription}`;

            case this.isFamily('plusminus', familyId):
                return this.toolkitDescription();

            case this.isFamily('sticks', familyId):
            case categoryId === CATEGORIES.TOOLKIT_ALT:
            case categoryId === CATEGORIES.TOOLKIT:
                return this.regularAndToolkitDescription();

            default:
                return this.props.shortDescription || '';
        }
    }

    /**
     * Describes the product from its configured attributes (DV-2591 format, as
     * built by sr-projects for the sr-crm sync path). Used for height-modified
     * (LW) products, whose article-master shortDescription always carries the
     * base-variation text (DV-6516).
     */
    private buildAttributesDescription(): string {
        const subFamilyName = this.props.subFamily?.description ?? '';
        const application = this.props.applicationLocation?.application?.description?.en ?? '';
        const finish = this.props.attributes?.finish?.description?.en ?? '';
        const temperature = this.props.attributes?.ledColourTemp?.description?.en ?? '';
        const control = this.props.attributes?.control?.description?.en ?? '';
        const installation = this.props.attributes?.installation?.description?.en ?? '';
        const pendant = this.props.attributes?.pendantHeight?.shortDescription?.en ?? '';

        return [subFamilyName, application, finish, temperature, control, installation, pendant]
            .filter(Boolean)
            .join(', ');
    }

    generateCrmDescription(): string {
        let description = this.getBaseCrmDescription();

        const pendantHeightValue = this.props.attributes?.pendantHeight?.value;
        const isRegularProduct = this.props.type === "REGULAR";

        if (pendantHeightValue && isRegularProduct) {
            const productCode = this.props.referenceCode || this.props.creationCode;
            // The "<referenceCode> <height> " prefix is parsed positionally by
            // web-api budget.service.transform.ts — do not change its shape.
            const attributesDescription = this.buildAttributesDescription() || description;
            description = `${productCode} ${pendantHeightValue} ${attributesDescription}`;
        }

        return description.trim();
    }

    setCrmDescription(): Product {
        this.props.crmDescription = this.generateCrmDescription();
        return this;
    }

    getProductCode(): string {
        return this.props.creationCode ?? this.props.articleCode ?? this.props.referenceCode;
    }

    private isUniqueCode(): boolean {
        const productCode = this.getProductCode();
        const regex = /LW/;
        return this.props.type === "CREATION" || regex.test(productCode);
    }

    private getProductType(): number {
        if (this.props.type === "CREATION") {
            if (this.props.category?.description === "VIRTUAL") {
                return PRODUCT_TYPE.VIRTUAL;
            } else if (this.props.category?.description === "TOOLKIT") {
                return PRODUCT_TYPE.TOOLKIT;
            } else {
                return PRODUCT_TYPE.REGULAR;
            }
        } else {
            return PRODUCT_TYPE.REGULAR;
        }
    }

    formatProductsForApiHub(): any {
        const shortDescription = this.props.crmDescription;
        const productCode = this.getProductCode();
        const isUniqueCode = this.isUniqueCode();
        const type = this.getProductType();

        return {
            type: type,
            description: shortDescription,
            quantity: this.props.quantity,
            unit_price: this.props.prices?.unitGross,
            is_catalog: !isUniqueCode,
            product_code: productCode
        };
    }

    static generateCrmDescriptionsForProducts(products: any[]): any[] {
        return products.map(productData => {
            const productCopy = JSON.parse(JSON.stringify(productData));
            const product = new Product(productCopy);
            product.setCrmDescription();
            return product.getProps();
        });
    }

    static formatProductsForApiHub(products: Product[]): any[] {
        return products.map(productData => {
            const product = new Product(productData);
            if (!product.props.crmDescription) {
                product.setCrmDescription();
            }
            return product.formatProductsForApiHub();
        });
    }

    static readonly PRODUCT_TYPE = PRODUCT_TYPE;
    static readonly CATEGORIES = CATEGORIES;
}